import React, { useCallback, useEffect, useMemo, useState } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { api, SettlementEntry, SettlementStatement } from '../api/client';
import { useAuth } from '../context/AuthContext';

function formatMoney(cents: number) {
  const sign = cents < 0 ? '-' : '';
  return `${sign}$${(Math.abs(cents) / 100).toFixed(2)}`;
}

function formatDateTime(value: string | null | undefined) {
  if (!value) return '—';
  return new Date(value).toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
}

type VenueSummary = { id: string; name: string; paymentRouting?: 'connect' | 'platform'; mercuryRecipientId?: string | null };

export default function SuperAdminSettlementLedgerScreen() {
  const { isSuperAdmin } = useAuth();
  const [venues, setVenues] = useState<VenueSummary[]>([]);
  const [selectedVenueId, setSelectedVenueId] = useState<string>('');
  const [entries, setEntries] = useState<SettlementEntry[]>([]);
  const [status, setStatus] = useState<'pending' | 'settled' | 'voided'>('pending');
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const [reference, setReference] = useState('');
  const [actionPending, setActionPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [statements, setStatements] = useState<SettlementStatement[]>([]);
  const [mercuryRecipientInput, setMercuryRecipientInput] = useState('');

  const selectedVenue = useMemo(
    () => venues.find((v) => v.id === selectedVenueId) || null,
    [venues, selectedVenueId]
  );

  useEffect(() => {
    setMercuryRecipientInput(selectedVenue?.mercuryRecipientId || '');
  }, [selectedVenue]);

  const defaultPeriodStart = useMemo(() => {
    const now = new Date();
    const prev = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 1, 1));
    return prev.toISOString().slice(0, 10);
  }, []);
  const [periodStart, setPeriodStart] = useState(defaultPeriodStart);

  const loadStatements = useCallback(async () => {
    if (!selectedVenueId) { setStatements([]); return; }
    try {
      const res = await api.listSettlementStatements(selectedVenueId);
      setStatements(res.statements);
    } catch (err: any) {
      setError(err.message || 'Failed to load statements');
    }
  }, [selectedVenueId]);

  useEffect(() => { loadStatements(); }, [loadStatements]);

  const handleGenerateStatement = async () => {
    if (!selectedVenueId) return;
    setActionPending(true); setError(null);
    try {
      const res = await api.generateSettlementStatement(selectedVenueId, periodStart);
      if (res.note) setError(res.note);
      await loadStatements();
    } catch (err: any) {
      setError(err.message || 'Failed to generate statement');
    } finally { setActionPending(false); }
  };

  const handleFinalize = async (id: string) => {
    setActionPending(true); setError(null);
    try { await api.finalizeSettlementStatement(id); await loadStatements(); }
    catch (err: any) { setError(err.message || 'Failed to finalize'); }
    finally { setActionPending(false); }
  };

  const handleMarkPaid = async (id: string) => {
    const ref = window.prompt('Check number / payment reference:');
    if (!ref) return;
    setActionPending(true); setError(null);
    try { await api.markSettlementStatementPaid(id, { paymentReference: ref }); await loadStatements(); await reload(); }
    catch (err: any) { setError(err.message || 'Failed to mark paid'); }
    finally { setActionPending(false); }
  };

  const handlePayViaMercury = async (statement: SettlementStatement) => {
    const confirmed = window.confirm(
      `Send ${formatMoney(statement.net_to_venue_cents)} to ${selectedVenue?.name || 'this venue'} via Mercury?\n\n`
      + 'This sends a real ACH transfer immediately and cannot be undone from here.'
    );
    if (!confirmed) return;
    setActionPending(true); setError(null);
    try {
      await api.paySettlementStatementViaMercury(statement.id);
      await loadStatements();
      await reload();
    } catch (err: any) {
      setError(err.message || 'Failed to pay via Mercury');
    } finally {
      setActionPending(false);
    }
  };

  const handleSaveMercuryRecipient = async () => {
    if (!selectedVenueId) return;
    const nextValue = mercuryRecipientInput.trim() || null;
    setActionPending(true); setError(null);
    try {
      await api.setSuperadminVenueMercuryRecipient(selectedVenueId, nextValue);
      setVenues((prev) => prev.map((v) => (
        v.id === selectedVenueId ? { ...v, mercuryRecipientId: nextValue } : v
      )));
    } catch (err: any) {
      setError(err.message || 'Failed to save Mercury recipient');
    } finally {
      setActionPending(false);
    }
  };

  // Filtered server-side to payment_routing = 'platform' — with thousands of
  // venues platform-wide, pulling a page and filtering client-side missed
  // any platform-collect venue that didn't happen to land alphabetically in
  // that page (this is how a single-venue search became "stuck" scrolling
  // through hundreds of unrelated Connect/request-to-book venues).
  const [venueSearch, setVenueSearch] = useState('');

  const loadVenues = useCallback(async (search?: string) => {
    try {
      const res = await api.getSuperadminVenues({ paymentRouting: 'platform', search: search || undefined, limit: 100 });
      const platformVenues = res.venues.map((v) => ({
        id: v.id,
        name: v.name,
        paymentRouting: (v as any).paymentRouting as 'connect' | 'platform' | undefined,
        mercuryRecipientId: (v as any).mercuryRecipientId as string | null | undefined,
      }));
      setVenues(platformVenues);
      setSelectedVenueId((current) => (
        platformVenues.some((v) => v.id === current) ? current : (platformVenues[0]?.id || '')
      ));
    } catch (err: any) {
      setError(err.message || 'Failed to load venues');
    }
  }, []);

  useEffect(() => {
    if (!isSuperAdmin) return;
    loadVenues();
  }, [isSuperAdmin, loadVenues]);

  useEffect(() => {
    if (!isSuperAdmin) return;
    const handle = setTimeout(() => loadVenues(venueSearch), 300);
    return () => clearTimeout(handle);
  }, [isSuperAdmin, venueSearch, loadVenues]);

  const reload = useCallback(async () => {
    if (!selectedVenueId) {
      setEntries([]);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await api.listSettlementEntries(selectedVenueId, { status, limit: 500 });
      setEntries(res.entries);
      setSelected({});
    } catch (err: any) {
      setError(err.message || 'Failed to load ledger');
    } finally {
      setLoading(false);
    }
  }, [selectedVenueId, status]);

  useEffect(() => { reload(); }, [reload]);

  const selectedIds = useMemo(() => Object.keys(selected).filter((k) => selected[k]), [selected]);
  const selectedTotals = useMemo(() => {
    let net = 0;
    let gross = 0;
    let commission = 0;
    for (const e of entries) {
      if (selected[e.id]) {
        net += e.net_to_venue_cents;
        gross += e.gross_cents;
        commission += e.commission_cents;
      }
    }
    return { net, gross, commission };
  }, [entries, selected]);

  const markSettled = async () => {
    if (selectedIds.length === 0) return;
    if (!reference.trim()) {
      alert('Enter a check number or batch reference first.');
      return;
    }
    setActionPending(true);
    try {
      const response = await api.markSettlementEntriesSettled(selectedVenueId, {
        entryIds: selectedIds,
        reference: reference.trim(),
      });
      setReference('');
      await reload();
      if (response.message) {
        setError(response.message);
      } else if ((response.skipped || []).length > 0) {
        setError(`${response.skipped!.length} entries were skipped because they are already attached to a statement.`);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to mark settled');
    } finally {
      setActionPending(false);
    }
  };

  const voidEntry = async (entryId: string) => {
    const notes = window.prompt('Reason for voiding this entry:');
    if (!notes || !notes.trim()) return;
    try {
      await api.voidSettlementEntry(entryId, notes.trim());
      await reload();
    } catch (err: any) {
      alert(err.message || 'Failed to void entry');
    }
  };

  if (!isSuperAdmin) {
    return (
      <DashboardLayout>
        <div className="card" style={{ padding: 32 }}>Superadmin access required.</div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="page-header">
        <div>
          <h1>Settlement Ledger</h1>
          <p>Platform-collect partner monthly settlement — paid by check or Mercury ACH.</p>
        </div>
      </div>

      <div className="card" style={{ padding: 20, marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center' }}>
          <label>
            <div style={{ fontSize: 11, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: 0.5 }}>Search platform-collect venues</div>
            <input
              type="text"
              value={venueSearch}
              onChange={(e) => setVenueSearch(e.target.value)}
              placeholder="Name, slug, or city"
              style={{ minWidth: 200, padding: '8px 12px', borderRadius: 8 }}
            />
          </label>
          <label>
            <div style={{ fontSize: 11, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: 0.5 }}>Venue</div>
            <select
              value={selectedVenueId}
              onChange={(e) => setSelectedVenueId(e.target.value)}
              style={{ minWidth: 280, padding: '8px 12px', borderRadius: 8 }}
            >
              <option value="">— Select a venue —</option>
              {venues.map((v) => (
                <option key={v.id} value={v.id}>{v.name}</option>
              ))}
            </select>
            {venues.length === 0 && (
              <div style={{ fontSize: 12, color: 'var(--gray-500)', marginTop: 4 }}>
                No platform-collect venues match{venueSearch ? ` "${venueSearch}"` : ''}.
              </div>
            )}
          </label>
          <label>
            <div style={{ fontSize: 11, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: 0.5 }}>Status</div>
            <select value={status} onChange={(e) => setStatus(e.target.value as any)} style={{ padding: '8px 12px', borderRadius: 8 }}>
              <option value="pending">Pending</option>
              <option value="settled">Settled</option>
              <option value="voided">Voided</option>
            </select>
          </label>
          {status === 'pending' && (
            <>
              <label style={{ flex: 1, minWidth: 240 }}>
                <div style={{ fontSize: 11, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: 0.5 }}>Check / batch reference</div>
                <input
                  type="text"
                  value={reference}
                  onChange={(e) => setReference(e.target.value)}
                  placeholder="e.g. CHK-1042"
                  style={{ width: '100%', padding: '8px 12px', borderRadius: 8 }}
                />
              </label>
              <button
                className="btn btn-primary"
                disabled={selectedIds.length === 0 || actionPending}
                onClick={markSettled}
                style={{ marginTop: 18 }}
              >
                {actionPending ? 'Marking…' : `Mark ${selectedIds.length} settled`}
              </button>
            </>
          )}
        </div>
        {selectedIds.length > 0 && (
          <div style={{ marginTop: 12, fontSize: 13, color: 'var(--gray-700)' }}>
            Selected total: <strong>{formatMoney(selectedTotals.net)}</strong> net ·
            {' '}gross {formatMoney(selectedTotals.gross)} · commission {formatMoney(selectedTotals.commission)}
          </div>
        )}
        {selectedVenueId && (
          <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid var(--gray-200)', display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <label style={{ flex: 1, minWidth: 280 }}>
              <div style={{ fontSize: 11, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Mercury recipient id for {selectedVenue?.name}
              </div>
              <input
                type="text"
                value={mercuryRecipientInput}
                onChange={(e) => setMercuryRecipientInput(e.target.value)}
                placeholder="Paste the recipient id from the Mercury dashboard"
                style={{ width: '100%', padding: '8px 12px', borderRadius: 8 }}
              />
            </label>
            <button
              className="btn btn-secondary"
              disabled={actionPending || mercuryRecipientInput.trim() === (selectedVenue?.mercuryRecipientId || '')}
              onClick={handleSaveMercuryRecipient}
            >
              Save
            </button>
            <div style={{ fontSize: 12, color: 'var(--gray-500)', width: '100%' }}>
              {selectedVenue?.mercuryRecipientId
                ? 'Finalized statements for this venue can be paid with one click below instead of mailing a check.'
                : 'Not configured — the recipient must already exist in Mercury; this only stores its id. Until set, statements are paid by check as usual.'}
            </div>
          </div>
        )}
      </div>

      {error && (
        <div className="card" style={{ padding: 16, marginBottom: 16, background: '#fef2f2', color: '#b91c1c' }}>
          {error}
        </div>
      )}

      {/* Monthly settlement statements — the primary check-run path. */}
      <div className="card" style={{ padding: 20, marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 12 }}>
          <div style={{ fontWeight: 600 }}>Settlement statements</div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input
              type="text"
              value={periodStart}
              onChange={(e) => setPeriodStart(e.target.value)}
              placeholder="YYYY-MM-01"
              style={{ padding: '6px 10px', border: '1px solid var(--gray-300)', borderRadius: 6, width: 130 }}
            />
            <button className="btn btn-secondary" disabled={!selectedVenueId || actionPending} onClick={handleGenerateStatement}>
              Generate / recompute draft
            </button>
          </div>
        </div>
        {statements.length === 0 ? (
          <div style={{ color: 'var(--gray-500)', fontSize: 13 }}>No statements yet for this venue.</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr>
                <th style={th()}>Period</th>
                <th style={th({ textAlign: 'right' })}>Entries</th>
                <th style={th({ textAlign: 'right' })}>Gross</th>
                <th style={th({ textAlign: 'right' })}>Commission</th>
                <th style={th({ textAlign: 'right' })}>Net to venue</th>
                <th style={th()}>Status</th>
                <th style={th()}>Method</th>
                <th style={th()}>Reference</th>
                <th style={th()}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {statements.map((s) => (
                <tr key={s.id}>
                  <td style={td()}>{s.period_start}</td>
                  <td style={td({ textAlign: 'right' })}>{s.entry_count}</td>
                  <td style={td({ textAlign: 'right' })}>{formatMoney(s.gross_cents)}</td>
                  <td style={td({ textAlign: 'right' })}>{formatMoney(s.commission_cents)}</td>
                  <td style={td({ textAlign: 'right' })}>{formatMoney(s.net_to_venue_cents)}</td>
                  <td style={td()}>{s.status}</td>
                  <td style={td()}>{s.payment_method === 'mercury_ach' ? 'Mercury ACH' : s.payment_method === 'check' ? 'Check' : (s.payment_method || '—')}</td>
                  <td style={td()}>{s.payment_reference || '—'}</td>
                  <td style={td()}>
                    {s.status === 'draft' && (
                      <button className="btn btn-secondary" disabled={actionPending} onClick={() => handleFinalize(s.id)}>Finalize</button>
                    )}
                    {s.status === 'finalized' && (
                      <div style={{ display: 'flex', gap: 6 }}>
                        {/* A zero (or negative) net has nothing to send: payStatementViaMercury
                            422s before it ever reaches Mercury. Hiding the button keeps a
                            real-money action — with an irreversible-ACH confirm dialog — off
                            a statement that can only fail, e.g. a month with no bookings.
                            net_to_venue_cents is BIGINT, so it arrives as a string. */}
                        {selectedVenue?.mercuryRecipientId && Number(s.net_to_venue_cents) > 0 && (
                          <button className="btn btn-primary" disabled={actionPending} onClick={() => handlePayViaMercury(s)}>Pay via Mercury</button>
                        )}
                        <button className="btn btn-secondary" disabled={actionPending} onClick={() => handleMarkPaid(s.id)}>Mark paid (check)</button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="card" style={{ padding: 0, overflowX: 'auto' }}>
        {loading ? (
          <div style={{ padding: 24 }}><div className="spinner" /></div>
        ) : entries.length === 0 ? (
          <div style={{ padding: 24, color: 'var(--gray-500)' }}>
            {selectedVenueId ? `No ${status} entries for this venue.` : 'Select a venue to view its ledger.'}
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'var(--gray-50)' }}>
                {status === 'pending' && <th style={th()}> </th>}
                <th style={th()}>Created</th>
                <th style={th()}>Type</th>
                <th style={th({ textAlign: 'right' })}>Gross</th>
                <th style={th({ textAlign: 'right' })}>Commission</th>
                <th style={th({ textAlign: 'right' })}>Net to venue</th>
                <th style={th()}>Reference</th>
                <th style={th()}>Notes</th>
                <th style={th()}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((entry) => (
                <tr key={entry.id} style={{ borderTop: '1px solid var(--gray-200)' }}>
                  {status === 'pending' && (
                    <td style={td()}>
                      <input
                        type="checkbox"
                        checked={!!selected[entry.id]}
                        onChange={(e) => setSelected((prev) => ({ ...prev, [entry.id]: e.target.checked }))}
                      />
                    </td>
                  )}
                  <td style={td()}>{formatDateTime(entry.created_at)}</td>
                  <td style={td()}>{entry.entry_type}</td>
                  <td style={td({ textAlign: 'right', fontVariantNumeric: 'tabular-nums' })}>{formatMoney(entry.gross_cents)}</td>
                  <td style={td({ textAlign: 'right', fontVariantNumeric: 'tabular-nums' })}>{formatMoney(entry.commission_cents)}</td>
                  <td style={td({ textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 600 })}>{formatMoney(entry.net_to_venue_cents)}</td>
                  <td style={td()}>{entry.settlement_reference || '—'}</td>
                  <td style={td()}>{entry.notes || '—'}</td>
                  <td style={td()}>
                    {entry.settlement_status === 'pending' && (
                      <button className="btn btn-secondary" onClick={() => voidEntry(entry.id)}>Void</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </DashboardLayout>
  );
}

function th(extra: React.CSSProperties = {}): React.CSSProperties {
  return {
    textAlign: 'left',
    padding: '10px 12px',
    fontSize: 11,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    color: 'var(--gray-500)',
    ...extra,
  };
}

function td(extra: React.CSSProperties = {}): React.CSSProperties {
  return {
    padding: '10px 12px',
    fontSize: 13,
    color: 'var(--gray-800)',
    verticalAlign: 'middle',
    ...extra,
  };
}
